import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Check, AlertCircle, Mail } from 'lucide-react';
import authService from '../../services/authService';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import LandingNav from '../Layout/LandingNav';
import LandingFooter from '../Layout/LandingFooter';
import SEO from '../SEO';

function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, refreshUser } = useAuth();
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link');
      return;
    }

    let hasVerified = false;

    const verifyEmail = async () => {
      if (hasVerified) return;
      hasVerified = true;

      try {
        await authService.verifyEmail(token);
        setStatus('success');
        setMessage('Email verified successfully!');
        toast.success('Email verified!');

        if (isAuthenticated) {
          try {
            await refreshUser();
            setTimeout(() => navigate('/'), 2000);
          } catch {
            setTimeout(() => navigate('/'), 2000);
          }
        } else {
          setTimeout(() => navigate('/login'), 3000);
        }
      } catch (err) {
        setStatus('error');
        setMessage(err.message || 'Verification failed');
        toast.error(err.message || 'Verification failed');
      }
    };

    verifyEmail();

    return () => { hasVerified = true; };
  }, [token, navigate, isAuthenticated, refreshUser]);

  const renderContent = () => {
    if (status === 'verifying') {
      return (
        <div className="">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className={`w-14 h-14 rounded-[14px] flex items-center justify-center ${d ? 'bg-white/[0.04]' : 'bg-gray-50'}`}>
                <Mail className={`w-6 h-6 animate-pulse ${d ? 'text-gray-400' : 'text-gray-400'}`} />
              </div>
            </div>
            <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>
              Verifying Email...
            </h2>
            <p className={`mt-2 text-sm ${d ? 'text-gray-500' : 'text-gray-500'}`}>
              Please wait while we verify your email address
            </p>
            <div className="mt-8 flex justify-center">
              <div className={`w-10 h-10 border-2 border-t-transparent rounded-full animate-spin ${d ? 'border-white/20 border-t-white' : 'border-gray-200 border-t-gray-900'}`} />
            </div>
          </div>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <>
          <div className="">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className={`w-14 h-14 rounded-[14px] flex items-center justify-center ${d ? 'bg-emerald-500/[0.08]' : 'bg-emerald-50'}`}>
                  <Check className={`w-6 h-6 ${d ? 'text-emerald-400' : 'text-emerald-500'}`} />
                </div>
              </div>
              <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>
                Email Verified!
              </h2>
              <p className={`mt-2 text-sm ${d ? 'text-gray-500' : 'text-gray-500'}`}>
                {message}
              </p>
              <p className={`mt-4 text-xs ${d ? 'text-gray-600' : 'text-gray-400'}`}>
                {isAuthenticated ? 'Redirecting to dashboard...' : 'Redirecting to login...'}
              </p>
            </div>

            <Link
              to={isAuthenticated ? '/' : '/login'}
              className={`
                mt-8 w-full flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium transition-all duration-200
                ${d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}
              `}
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Continue to Sign In'}
            </Link>
          </div>
        </>
      );
    }

    // Error state
    return (
      <>
        <div className="">
          <div className="text-center">
            <div className="flex justify-center mb-6">
              <div className={`w-14 h-14 rounded-[14px] flex items-center justify-center ${d ? 'bg-red-500/[0.08]' : 'bg-red-50'}`}>
                <AlertCircle className={`w-6 h-6 ${d ? 'text-red-400' : 'text-red-500'}`} />
              </div>
            </div>
            <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight ${d ? 'text-white' : 'text-gray-900'}`}>
              Verification Failed
            </h2>
            <p className={`mt-2 text-sm ${d ? 'text-gray-500' : 'text-gray-500'}`}>
              {message}
            </p>
          </div>

          <div className={`mt-6 rounded-[12px] border p-3.5 ${d ? 'bg-amber-500/[0.06] border-amber-500/10' : 'bg-amber-50 border-amber-100'}`}>
            <p className={`text-sm ${d ? 'text-amber-400/80' : 'text-amber-600'}`}>
              The verification link may have expired or is invalid.
            </p>
          </div>

          <Link
            to="/login"
            className={`
              mt-6 w-full flex items-center justify-center py-3 px-4 rounded-[12px] text-sm font-medium transition-all duration-200
              ${d ? 'bg-white text-black hover:bg-gray-200' : 'bg-black text-white hover:bg-gray-800'}
            `}
          >
            Go to Sign In
          </Link>
        </div>
      </>
    );
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${d ? 'bg-[#0a0a0a]' : 'bg-white'}`}>
      <SEO
        title="Verify email"
        description="Verify your PaperBrainz account email address."
        path="/verify-email"
        noindex
      />
      <LandingNav />

      <div className="flex-1 flex items-center justify-center px-4 pt-44 pb-32">
        <div className="relative z-10 w-full max-w-md">
          {renderContent()}
        </div>
      </div>

      <LandingFooter />
    </div>
  );
}

export default VerifyEmailPage;
